<template>
  <div @click="onCLick">

    <bvc-upload
      ref="upload"
      accept="image/*"
      :capture="capture"
      :file-type-regex="/^image\/([a-zA-z]).*/"
      :size="maxSize"
      :multiple="multiple"
      @input="onUploadInput"
      @error="onUploadError"
    />

    <slot/>

  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import BvcUpload from '@/booksy-vue-components/components/b-upload/index.vue'
import { IMAGE_OPTIONS } from '@/lib/consts.ts'
import {
  ERROR_FILE_TOO_BIG,
  ERROR_FILE_FORMAT,
  ERROR_TOO_MANY_FILES,
} from '@/booksy-vue-components/components/b-upload/consts'
import { useToast } from '@/composables/toast'

const SIZE = 10

export default defineComponent({

  emits: [
    'click',
    'input',
  ],
  components: {
    BvcUpload,
  },

  props: {
    // use capture device (camera) instead file browser
    capture: {
      type: Boolean,
      default: false,
    },

    // allows to add multiple photos to input
    multiple: {
      type: Boolean,
      default: false,
    },

    imageOptions: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => { return IMAGE_OPTIONS },
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    const toast = useToast()

    return {
      toast,
    }
  },

  data () {
    return {
      maxSize: SIZE,
    }
  },

  methods: {

    onCLick (e) {
      if (this.disabled) return
      (this.$refs.upload as { init: () => void }).init()
      this.$emit('click', e)
    },

    onUploadInput (file) {
      // blueimp image load options
      const loadOptions = {
        canvas: true,
        maxWidth: this.imageOptions.maxWidth,
        maxHeight: this.imageOptions.maxHeight,
        orientation: true,
      }

      const blueimp = import('blueimp-load-image')

      if (this.multiple) {
        const files = file

        return blueimp.then(loadImage => {
          Promise.all(files.map(f => this.handleImageLoad(loadImage, loadOptions, f)))
            .then(loadedFiles => {
              // emit images array
              this.$emit('input', loadedFiles);

              // reset input value
              (this.$refs.upload as { reset: () => void }).reset()
            })
        })
      }

      // lazy import (this component is used occasionally)
      return blueimp
        .then(loadImage => {
          loadImage.default(
            file,
            (canvas, data) => {
              // emit image
              this.$emit('input', {
                file,
                url: canvas.toDataURL(),
              });

              // reset input value
              (this.$refs.upload as { reset: () => void }).reset()
            },
            loadOptions
          )
        })
    },

    handleImageLoad (loadImage, loadOptions, file) {
      return new Promise((resolve, reject) => {
        loadImage.default(
          file,
          (canvas, data) => {
            // emit image
            resolve({
              file,
              url: canvas.toDataURL(),
            })
          },
          loadOptions
        )
      })
    },

    onUploadError ({ error, size }) {
      if (error === ERROR_FILE_TOO_BIG) {
        this.toast.error(this.$tc('form-errors.file-to-big', { size }))
      }
      if (error === ERROR_FILE_FORMAT) {
        this.toast.error(this.$t('form-errors.file-wrong-format'))
      }
      if (error === ERROR_TOO_MANY_FILES) {
        this.toast.error(this.$t('form-errors.too-many-files'))
      }
    },
  },
})

</script>
